import { useMemo } from 'react';

import dayjs from 'dayjs';
import type { ComputeResourceTypeValue } from '@cast/shared-types';
import type { WorkloadsSummaryMetrics } from '@cast/types';
import { useWorkloadsSummaryMetricsQuery } from '@cast/features-cluster-shared';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

type InventoryWorkloadsMetricsChartItem = {
  originalReq: number;
  req: number;
  usage: number;
  rec: number;
  diff: number;
  timestamp: string;
};

export type InventoryWorkloadsMetricsChartData = {
  cpu: InventoryWorkloadsMetricsChartItem[];
  memory: InventoryWorkloadsMetricsChartItem[];
};

type BaseQueryResult = Omit<
  ReturnType<typeof useWorkloadsSummaryMetricsQuery>,
  'data'
>;

type ConvertToChartItemArgs = {
  data: WorkloadsSummaryMetrics;
  resource: ComputeResourceTypeValue;
};

const convertToChartItem = ({
  data,
  resource,
}: ConvertToChartItemArgs): InventoryWorkloadsMetricsChartItem => {
  const originalReq =
    resource === 'cpu'
      ? data.cpuOriginalRequestCores
      : data.memoryOriginalRequestGibs;
  const req =
    resource === 'cpu' ? data.cpuRequestCores : data.memoryRequestGibs;
  const usage = resource === 'cpu' ? data.cpuUsageCores : data.memoryUsageGibs;
  const rec =
    resource === 'cpu'
      ? data.cpuRecommendationCores
      : data.memoryRecommendationGibs;

  return {
    originalReq,
    req,
    usage,
    rec,
    diff: originalReq - req,
    timestamp: data.timestamp,
  };
};

export const useInventoryWorkloadsMetrics = (): BaseQueryResult & {
  dataOrFixture: InventoryWorkloadsMetricsChartData | undefined;
} => {
  const { cluster } = useCluster();
  const { getRange } = useInventoryWorkloadsTimeRangeSelection();

  const { fromTime, toTime } = useMemo(() => {
    const range = getRange();
    return {
      fromTime: dayjs(range.from).toISOString(),
      toTime: dayjs(range.to).toISOString(),
    };
  }, [getRange]);

  const { data, ...restQueryResult } = useWorkloadsSummaryMetricsQuery({
    clusterId: cluster.id,
    enabled: !!cluster.id,
    fromTime,
    toTime,
  });

  const transformedData = useMemo(
    () =>
      data && {
        cpu: data.items.map((d) =>
          convertToChartItem({ data: d, resource: 'cpu' })
        ),
        memory: data.items.map((d) =>
          convertToChartItem({ data: d, resource: 'memory' })
        ),
      },
    [data]
  );

  return {
    ...restQueryResult,
    dataOrFixture: transformedData,
  };
};
